import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Search,
  Filter,
  Plus,
  Mail,
  Phone,
  Users,
  Star,
  Eye,
  Trash2,
  Edit,
  UserCheck,
  UserX,
  TrendingUp,
  Clock,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import AddCounselorDialog from '../../components/admin/AddCounselorDialog';
import EditCounselorDialog from '../../components/admin/EditCounselorDialog';
import DeleteCounselorDialog from '../../components/admin/DeleteCounselorDialog';

export default function ManageCounselors() {
  const navigate = useNavigate();
  const [counselors, setCounselors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    totalActiveAssignments: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    pages: 0,
  });

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCounselor, setSelectedCounselor] = useState(null);

  useEffect(() => {
    fetchCounselors();
    fetchStats();
  }, [statusFilter, roleFilter, pagination.page]);

  const fetchCounselors = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCounselors({
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter,
        role: roleFilter,
        search: searchTerm,
      });
      setCounselors(response.counselors || []);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        pages: response.pagination?.pages || 0,
      }));
    } catch (error) {
      toast.error('Failed to fetch counselors');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getCounselorStats();
      if (response.stats) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchCounselors();
  };

  const handleStatusToggle = async (counselor) => {
    try {
      const newStatus = !counselor.isActive;
      await adminAPI.updateCounselorStatus(counselor._id, newStatus);
      toast.success(`Counselor ${newStatus ? 'activated' : 'deactivated'}`);
      fetchCounselors();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleEdit = (counselor) => {
    setSelectedCounselor(counselor);
    setEditDialogOpen(true);
  };

  const handleDelete = (counselor) => {
    setSelectedCounselor(counselor);
    setDeleteDialogOpen(true);
  };

  const handleView = (counselor) => {
    navigate(`/admin/counselors/${counselor._id}`);
  };

  const handleDialogSuccess = () => {
    fetchCounselors();
    fetchStats();
  };

  const filteredCounselors = counselors.filter(counselor => {
    const name = `${counselor.firstName || ''} ${counselor.lastName || ''}`.toLowerCase();
    const email = (counselor.email || '').toLowerCase();
    return name.includes(searchTerm.toLowerCase()) || email.includes(searchTerm.toLowerCase());
  });

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="manage-counselors-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900">Manage Counselors</h1>
            <p className="text-gray-600">View and manage all counselors and advisors</p>
          </div>
          <Button onClick={() => setAddDialogOpen(true)} data-testid="add-counselor-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Counselor
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Counselors</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <UserX className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Inactive</p>
                <p className="text-2xl font-bold text-gray-900">{stats.inactive}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Assignments</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalActiveAssignments}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Search and Filter */}
        <Card className="p-4 bg-white border-stone-200/80">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search counselors by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-counselors-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="counselor">Counselor</SelectItem>
                <SelectItem value="advisor">Advisor</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" variant="outline" data-testid="filter-counselors-btn">
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
          </form>
        </Card>

        {/* Counselors Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-[#F2575F] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredCounselors.length === 0 ? (
          <Card className="flex flex-col items-center justify-center h-64 text-gray-500 bg-white border-stone-200/80">
            <Users className="w-12 h-12 mb-4 text-gray-300" />
            <p className="text-lg font-medium">No counselors found</p>
            <p className="text-sm">Try adjusting your search or add a new counselor</p>
            <Button className="mt-4" onClick={() => setAddDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add First Counselor
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="counselors-grid">
            {filteredCounselors.map((counselor, index) => (
              <motion.div
                key={counselor._id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="p-6 bg-white border-stone-200/80 hover:shadow-lg hover:shadow-stone-200/50 transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#F2575F] to-[#E04850] rounded-full flex items-center justify-center text-white font-semibold text-lg">
                        {counselor.firstName?.[0]}{counselor.lastName?.[0]}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {counselor.firstName} {counselor.lastName}
                        </h3>
                        <div className="flex items-center gap-2">
                          <Badge className={`text-xs ${
                            counselor.role === 'advisor'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {counselor.role === 'advisor' ? 'Advisor' : 'Counselor'}
                          </Badge>
                          <Badge className={`text-xs ${
                            counselor.isActive !== false
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {counselor.isActive !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={counselor.isActive !== false}
                      onCheckedChange={() => handleStatusToggle(counselor)}
                      className="data-[state=checked]:bg-green-500"
                    />
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{counselor.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4" />
                      <span>{counselor.phone || 'Not provided'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Users className="w-4 h-4" />
                      <span>{counselor.totalStudents || 0} students assigned</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{counselor.activeAssignments || 0} active assignments</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-4 border-t border-stone-100">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleView(counselor)}
                      data-testid={`view-counselor-${index}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleEdit(counselor)}
                      data-testid={`edit-counselor-${index}`}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(counselor)}
                      data-testid={`delete-counselor-${index}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && filteredCounselors.length > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {filteredCounselors.length} of {pagination.total} counselors
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.pages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= pagination.pages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <AddCounselorDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSuccess={handleDialogSuccess}
      />

      <EditCounselorDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        counselor={selectedCounselor}
        onSuccess={handleDialogSuccess}
      />

      <DeleteCounselorDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        counselor={selectedCounselor}
        onSuccess={handleDialogSuccess}
      />
    </AdminLayout>
  );
}
